<template>
    <div class="min-h-screen bg-white">
        <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div v-if="loading" class="text-center py-3">
                <Loader size="large" />
            </div>
            <template v-else>
                <div class="text-center mb-2.5">
                    <h1 class="font-header text-[3rem] xl:text-[3rem] font-bold text-primary mb-1.5">
                        Frequently Asked Questions
                    </h1>
                    <div v-if="pageDescription"
                        class="font-body text-[1.125rem] text-gray-700 max-w-4xl mx-auto leading-[1.75] content-page-description"
                        v-html="pageDescription">
                    </div>
                </div>

                <div v-if="faqs.length === 0" class="text-center py-3">
                    <p class="font-body text-[1.125rem] text-gray-500 leading-[1.75]">No FAQs available at the moment.
                    </p>
                </div>

                <div v-else class="space-y-1">
                    <div v-for="faq in sortedFaqs" :key="faq.id"
                        class="bg-white rounded-lg shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md">
                        <button @click="toggleFaq(faq.id)"
                            class="w-full px-6 py-1 text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-green-700 focus:ring-offset-2 transition-colors"
                            :class="openFaqs.includes(faq.id) ? 'bg-green-50' : 'hover:bg-creme-50'">
                            <span class="font-header text-[1.125rem] font-semibold text-primary pr-4 flex-1">
                                {{ faq.question }}
                            </span>
                            <svg class="w-6 h-6 text-green-700 flex-shrink-0 transition-transform duration-300"
                                :class="{ 'rotate-180': openFaqs.includes(faq.id) }" fill="none" stroke="currentColor"
                                viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        <div v-show="openFaqs.includes(faq.id)" class="px-6 pb-1.5 pt-0 border-t border-gray-100">
                            <div class="pt-1 font-body text-[1.125rem] text-gray-700 leading-[1.75] content-page-section"
                                v-html="faq.answer">
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { publicService } from '../services/public'
import Loader from './Loader.vue'
import { useLoadingState } from '../composables/useLoadingState'

const faqs = ref([])
const pageDescription = ref('')
const openFaqs = ref([])
const { loading, setLoading } = useLoadingState(true)

const sortedFaqs = computed(() => {
    return [...faqs.value].sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
})

const toggleFaq = (id) => {
    const index = openFaqs.value.indexOf(id)
    if (index > -1) {
        openFaqs.value.splice(index, 1)
    } else {
        openFaqs.value.push(id)
    }
}

const loadFaqs = async () => {
    setLoading(true)
    try {
        const data = await publicService.getFaqs()
        faqs.value = Array.isArray(data) ? data : []
    } catch (error) {
        console.error('Failed to load FAQs:', error)
        faqs.value = []
    } finally {
        setLoading(false)
    }
}

const loadPageDescription = async () => {
    try {
        const page = await publicService.getPage('faq')
        pageDescription.value = page.description || ''
    } catch (error) {
        console.error('Failed to load page description:', error)
        pageDescription.value = ''
    }
}

onMounted(() => {
    loadFaqs()
    loadPageDescription()
})
</script>

<style scoped>
/* Links */
:deep(a) {
    color: #2563eb;
    text-decoration: underline;
    transition: color 0.2s;
}

:deep(a):hover {
    color: #1e40af;
}

/* Lists */
:deep(ul) {
    list-style-type: disc;
    margin-left: 1.5em;
    margin-bottom: 0.25em;
}

:deep(ol) {
    list-style-type: decimal;
    margin-left: 1.5em;
    margin-bottom: 0.25em;
}

:deep(li) {
    margin-bottom: 0.125em;
}

/* Headers */
:deep(h1) {
    font-size: 2rem;
    font-weight: bold;
    margin-top: 1.5rem;
    margin-bottom: 1rem;
    color: #2D4D31;
}

:deep(h2) {
    font-size: 1.75rem;
    font-weight: bold;
    margin-top: 1.25rem;
    margin-bottom: 0.875rem;
    color: #2D4D31;
}

:deep(h3) {
    font-size: 1.5rem;
    font-weight: bold;
    margin-top: 1rem;
    margin-bottom: 0.75rem;
    color: #2D4D31;
}

/* Font Sizes */
:deep(.ql-size-small) {
    font-size: 0.875rem;
}

:deep(.ql-size-large) {
    font-size: 1.25rem;
}

:deep(.ql-size-huge) {
    font-size: 1.5rem;
}

/* Blockquotes */
:deep(blockquote) {
    border-left: 4px solid #2D4D31;
    padding-left: 1rem;
    margin: 1rem 0;
    font-style: italic;
    color: #5B4F47;
}

/* Code Blocks */
:deep(pre) {
    background-color: #f5f5f5;
    border: 1px solid #ddd;
    border-radius: 0.25rem;
    padding: 1rem;
    overflow-x: auto;
    margin: 1rem 0;
    font-family: 'Courier New', monospace;
}

:deep(code) {
    background-color: #f5f5f5;
    border: 1px solid #ddd;
    border-radius: 0.25rem;
    padding: 0.125rem 0.375rem;
    font-family: 'Courier New', monospace;
    font-size: 0.875rem;
}

:deep(pre code) {
    background: none;
    border: none;
    padding: 0;
}

/* Images */
:deep(img) {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 1.5rem auto;
    border-radius: 0.5rem;
}

/* Videos */
:deep(iframe) {
    max-width: 100%;
    margin: 1.5rem auto;
    display: block;
    border-radius: 0.5rem;
}

:deep(.ql-video) {
    width: 100%;
    max-width: 640px;
    height: 360px;
    margin: 1.5rem auto;
    display: block;
}

/* Text Alignment */
:deep(.ql-align-center) {
    text-align: center;
}

:deep(.ql-align-right) {
    text-align: right;
}

:deep(.ql-align-left) {
    text-align: left;
}

:deep(.ql-align-justify) {
    text-align: justify;
}

/* Inline styles support */
:deep([style*="text-align: center"]),
:deep([style*="text-align:center"]) {
    text-align: center !important;
}

:deep([style*="text-align: right"]),
:deep([style*="text-align:right"]) {
    text-align: right !important;
}

:deep([style*="text-align: left"]),
:deep([style*="text-align:left"]) {
    text-align: left !important;
}

:deep([style*="text-align: justify"]),
:deep([style*="text-align:justify"]) {
    text-align: justify !important;
}

/* Font Families */
:deep(.ql-font-serif) {
    font-family: Georgia, 'Times New Roman', serif;
}

:deep(.ql-font-monospace) {
    font-family: 'Courier New', monospace;
}

/* Spacing */
:deep(p) {
    margin-bottom: 0.75rem;
}

:deep(p:last-child) {
    margin-bottom: 0;
}
</style>